import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { App, Checkbox, Drawer, Modal, Radio } from 'antd';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { NativeSelect } from '../../../shared/DropdownControls';
import { HoverImagePreview } from '../../../shared/HoverImagePreview';
import {
  CASE_SOURCE_OPTIONS,
  type CaseShowcaseFixture,
  type CaseShowcaseRecord,
  createCaseShowcaseFixture,
  DEFAULT_CASE_SOURCE,
} from '../mock/caseShowcase';
import {
  readCaseShowcaseMockCases,
  readCaseShowcaseMockPlanRelations,
  saveCaseShowcaseMockPlanRelations,
  saveCaseShowcaseMockState,
} from '../mock/caseShowcaseSession';
import type { PlanCatalogRecord } from '../state/types';
import styles from './CaseShareAdminPage.module.css';
import { ManagementFilterBar } from './ManagementFilterBar';

type AdminTab = 'cases' | 'case-shares';
type CaseShareMode = 'shared' | 'separate';
type NavigationIntent =
  | { type: 'list' }
  | { type: 'tab'; tab: AdminTab }
  | { type: 'case'; caseId: string }
  | { type: 'external'; proceed: () => void };

interface CaseShareExample {
  id: string;
  caseIds: string[];
  mode: CaseShareMode;
}

type DetailState = { kind: 'case-share'; id: string } | null;

function sameIds(left: readonly string[], right: readonly string[]) {
  return (
    left.length === right.length &&
    [...left].sort().every((id, index) => id === [...right].sort()[index])
  );
}

function commonPlanIds(
  caseIds: readonly string[],
  relations: Record<string, string[]>,
) {
  const [first, ...rest] = caseIds;
  if (!first) return [];
  return (relations[first] ?? []).filter((planId) =>
    rest.every((caseId) => (relations[caseId] ?? []).includes(planId)),
  );
}

function planDeviceNames(fixture: CaseShowcaseFixture, planId: string) {
  const deviceIds = new Set(
    fixture.catalog.packages
      .filter((item) => item.planIds.includes(planId))
      .flatMap((item) => item.productLinks.map((link) => link.deviceId)),
  );
  return [...deviceIds]
    .map(
      (deviceId) =>
        fixture.catalog.devices.find((device) => device.id === deviceId)?.name,
    )
    .filter((name): name is string => Boolean(name));
}

function planLabel(plan: PlanCatalogRecord) {
  return `${plan.code ?? plan.id} · ${plan.name}`;
}

function caseName(caseRecord: CaseShowcaseRecord | undefined) {
  return caseRecord?.name ?? '未知案例';
}

// 列表展示用：案例分类 + 标签，去掉与案例同名的标签和"未分类"占位。
function caseTagLabels(record: CaseShowcaseRecord) {
  return [
    ...new Set(
      [
        record.category !== record.name ? record.category : '',
        ...record.tags.filter((tag) => tag !== record.name),
      ].filter((tag) => tag && tag !== '未分类'),
    ),
  ];
}

function planSearchText(plan: PlanCatalogRecord) {
  return `${plan.code ?? plan.id} ${plan.name}`.toLowerCase();
}

function nextCaseId(cases: readonly CaseShowcaseRecord[]) {
  const used = cases
    .map((record) => Number.parseInt(record.caseId.replace(/\D/g, ''), 10))
    .filter((value) => Number.isFinite(value));
  const next = (used.length > 0 ? Math.max(...used) : 0) + 1;
  return `CASE-${String(next).padStart(3, '0')}`;
}

function PlanLinkAdder({
  options,
  onPick,
  label,
}: {
  options: { plan: PlanCatalogRecord; linkedCaseCount: number }[];
  onPick(planId: string): void;
  label: string;
}) {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const term = query.trim().toLowerCase();
  const candidates = term
    ? options.filter(({ plan }) => planSearchText(plan).includes(term))
    : options;
  const optionsOpen = focused || term.length > 0;

  return (
    <div className={styles.planAdder}>
      <label className={styles.searchField}>
        <span className={styles.visuallyHidden}>{label}</span>
        <input
          aria-label={label}
          placeholder="点击展开方案，或输入编号名称筛选"
          type="search"
          value={query}
          onBlur={() => setFocused(false)}
          onChange={(event) => setQuery(event.currentTarget.value)}
          onFocus={() => setFocused(true)}
        />
      </label>
      {optionsOpen ? (
        <ul
          className={styles.planOptions}
          /* 保持输入框焦点，点击候选时下拉不会先收起。 */
          onMouseDown={(event) => event.preventDefault()}
        >
          {candidates.map(({ plan, linkedCaseCount }) => (
            <li key={plan.id}>
              <button
                className={styles.planOption}
                type="button"
                onClick={() => {
                  onPick(plan.id);
                  setQuery('');
                }}
              >
                <span className={styles.codeCell}>{plan.code ?? plan.id}</span>
                <span className={styles.planOptionName}>{plan.name}</span>
                <span className={styles.subCell}>
                  {linkedCaseCount} 个案例已关联
                </span>
              </button>
            </li>
          ))}
          {candidates.length === 0 ? (
            <li className={styles.planOptionEmpty}>
              {options.length === 0
                ? '当前案例已关联全部方案。'
                : '未找到匹配的方案，请调整搜索词。'}
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

export function CaseShareAdminPage() {
  const { message, modal } = App.useApp();
  const [fixture] = useState(createCaseShowcaseFixture);
  const [cases, setCases] = useState<CaseShowcaseRecord[]>(() =>
    readCaseShowcaseMockCases(fixture),
  );
  const [tab, setTab] = useState<AdminTab>('cases');
  const [expandedCaseIds, setExpandedCaseIds] = useState<string[]>([]);
  const [activeCaseId, setActiveCaseId] = useState(cases[0]?.id ?? '');
  const [initialRelations] = useState(() =>
    readCaseShowcaseMockPlanRelations(fixture),
  );
  const [savedRelations, setSavedRelations] = useState(initialRelations);
  const [caseQueryInput, setCaseQueryInput] = useState('');
  const [caseQuery, setCaseQuery] = useState('');
  const [caseSourceInput, setCaseSourceInput] = useState('all');
  const [caseSource, setCaseSource] = useState('all');
  const [caseRelationFilterInput, setCaseRelationFilterInput] = useState('all');
  const [caseRelationFilter, setCaseRelationFilter] = useState('all');
  const [caseShareQueryInput, setCaseShareQueryInput] = useState('');
  const [caseShareQuery, setCaseShareQuery] = useState('');
  const [caseShareModeInput, setCaseShareModeInput] = useState('all');
  const [caseShareMode, setCaseShareMode] = useState('all');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'create' | 'edit'>('edit');
  const [caseForm, setCaseForm] = useState({
    caseId: '',
    name: '',
    source: DEFAULT_CASE_SOURCE,
    tags: [] as string[],
    images: [] as string[],
  });
  const [formPlanIds, setFormPlanIds] = useState<string[]>([]);
  const [caseFormError, setCaseFormError] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [relationError, setRelationError] = useState('');
  const [pendingNavigation, setPendingNavigation] =
    useState<NavigationIntent | null>(null);
  const [detail, setDetail] = useState<DetailState>(null);

  const activeCase = cases.find((record) => record.id === activeCaseId);
  const editingCase = editorMode === 'edit' ? activeCase : undefined;
  const isEditorDirty =
    editorOpen &&
    (editingCase
      ? caseForm.caseId !== editingCase.caseId ||
        caseForm.name !== editingCase.name ||
        caseForm.source !== (editingCase.source ?? DEFAULT_CASE_SOURCE) ||
        !sameIds(caseForm.tags, editingCase.tags) ||
        !sameIds(caseForm.images, editingCase.gallery)
      : caseForm.name.trim() !== '' ||
        caseForm.images.length > 0 ||
        formPlanIds.length > 0);

  useEffect(() => {
    const handleBeforeLeave = (event: Event) => {
      if (!isEditorDirty) return;
      const navigationEvent = event as CustomEvent<{
        proceed: () => void;
      }>;
      navigationEvent.preventDefault();
      setPendingNavigation({
        type: 'external',
        proceed: navigationEvent.detail.proceed,
      });
    };

    window.addEventListener('cpq-case-admin-before-leave', handleBeforeLeave);
    return () =>
      window.removeEventListener(
        'cpq-case-admin-before-leave',
        handleBeforeLeave,
      );
  }, [isEditorDirty]);

  useEffect(() => {
    if (!isEditorDirty) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isEditorDirty]);

  // 初始化一次后独立维护：删除或编辑案例不再改写已保存的分享记录。
  const [caseShares, setCaseShares] = useState<CaseShareExample[]>(() => [
    {
      id: 'CS-001',
      caseIds: cases.slice(0, 2).map((record) => record.id),
      mode: 'shared',
    },
    {
      id: 'CS-002',
      caseIds: cases.slice(2, 4).map((record) => record.id),
      mode: 'separate',
    },
  ]);
  const [shareEditorOpen, setShareEditorOpen] = useState(false);
  const [editingShareId, setEditingShareId] = useState<string | null>(null);
  const [shareDraftCaseIds, setShareDraftCaseIds] = useState<string[]>([]);
  const [shareDraftMode, setShareDraftMode] = useState<CaseShareMode>('shared');
  const [shareDraftError, setShareDraftError] = useState('');
  const filteredCases = useMemo(() => {
    const query = caseQuery.trim().toLowerCase();
    return cases.filter((record) => {
      const matchesQuery =
        !query ||
        `${record.caseId} ${record.caseCode} ${record.name}`
          .toLowerCase()
          .includes(query);
      const matchesSource =
        caseSource === 'all' ||
        (record.source ?? DEFAULT_CASE_SOURCE) === caseSource;
      const relationCount = (savedRelations[record.id] ?? []).length;
      const matchesRelation =
        caseRelationFilter === 'all' ||
        (caseRelationFilter === 'linked' && relationCount > 0) ||
        (caseRelationFilter === 'unlinked' && relationCount === 0);
      return matchesQuery && matchesSource && matchesRelation;
    });
  }, [caseQuery, caseRelationFilter, caseSource, cases, savedRelations]);
  const activeCasePosition = filteredCases.findIndex(
    (record) => record.id === activeCaseId,
  );
  const previousCase =
    activeCasePosition > 0 ? filteredCases[activeCasePosition - 1] : undefined;
  const nextCase =
    activeCasePosition >= 0 && activeCasePosition < filteredCases.length - 1
      ? filteredCases[activeCasePosition + 1]
      : undefined;

  const filteredCaseShares = caseShares.filter((share) => {
    const query = caseShareQuery.trim().toLowerCase();
    const names = share.caseIds
      .map((id) => caseName(cases.find((record) => record.id === id)))
      .join(' ');
    return (
      (!query || `${share.id} ${names}`.toLowerCase().includes(query)) &&
      (caseShareMode === 'all' || share.mode === caseShareMode)
    );
  });
  const activeCaseShare =
    detail?.kind === 'case-share'
      ? caseShares.find((record) => record.id === detail.id)
      : undefined;
  const detailTitle = activeCaseShare ? `案例分享 · ${activeCaseShare.id}` : '';

  const loadCaseIntoForm = (record: CaseShowcaseRecord) => {
    setActiveCaseId(record.id);
    setCaseForm({
      caseId: record.caseId,
      name: record.name,
      source: record.source ?? DEFAULT_CASE_SOURCE,
      tags: [...record.tags],
      images: [...record.gallery],
    });
    setFormPlanIds([...(savedRelations[record.id] ?? [])]);
  };

  const saveEditor = () => {
    const caseId = caseForm.caseId.trim();
    const name = caseForm.name.trim();
    if (!caseId) {
      setCaseFormError('请填写案例编号。');
      return false;
    }
    if (!name) {
      setCaseFormError('请填写案例名称。');
      return false;
    }
    if (caseForm.images.length === 0) {
      setCaseFormError('请上传至少一张效果图。');
      return false;
    }
    const duplicated = cases.some(
      (record) =>
        record.caseId === caseId &&
        (editorMode === 'create' || record.id !== activeCaseId),
    );
    if (duplicated) {
      setCaseFormError('案例编号已存在，请更换。');
      return false;
    }

    const nextCases =
      editorMode === 'edit'
        ? cases.map((record) =>
            record.id === activeCaseId
              ? {
                  ...record,
                  caseId,
                  name,
                  source: caseForm.source,
                  tags: [...caseForm.tags],
                  category: caseForm.tags[0] ?? '',
                  heroImage: caseForm.images[0] ?? record.heroImage,
                  gallery: [...caseForm.images],
                }
              : record,
          )
        : [
            ...cases,
            {
              id: `${caseId}:V1`,
              caseId,
              caseCode: caseId,
              version: 1 as const,
              name,
              category: caseForm.tags[0] ?? '',
              summary: '后台新增的演示案例。',
              targetCustomer: name,
              source: caseForm.source,
              tags: [...caseForm.tags],
              planIds: [],
              heroImage: caseForm.images[0] ?? '',
              gallery: [...caseForm.images],
            },
          ];
    const relationCaseId =
      editorMode === 'edit' ? activeCaseId : `${caseId}:V1`;
    const nextRelations = {
      ...savedRelations,
      [relationCaseId]: [...new Set(formPlanIds)],
    };

    try {
      saveCaseShowcaseMockState(nextCases, nextRelations);
    } catch {
      setCaseFormError('浏览器会话存储不可用，案例未保存。');
      return false;
    }
    setCases(nextCases);
    setSavedRelations(nextRelations);
    setCaseFormError('');
    setEditorOpen(false);
    message.success(editorMode === 'create' ? '案例已新增' : '案例已保存');
    return true;
  };

  const applyNavigation = (intent: NavigationIntent) => {
    if (intent.type === 'external') {
      setPendingNavigation(null);
      intent.proceed();
      return;
    }
    if (intent.type === 'list') {
      setEditorOpen(false);
    } else if (intent.type === 'tab') {
      setTab(intent.tab);
    } else if (intent.type === 'case') {
      const target = cases.find((record) => record.id === intent.caseId);
      if (target) loadCaseIntoForm(target);
      setEditorOpen(true);
    }
    setPendingNavigation(null);
  };

  const requestNavigation = (intent: NavigationIntent) => {
    if (
      (intent.type === 'list' && !editorOpen) ||
      (intent.type === 'tab' && intent.tab === tab) ||
      (intent.type === 'case' && intent.caseId === activeCaseId && editorOpen)
    ) {
      return;
    }
    if (isEditorDirty) {
      setPendingNavigation(intent);
      return;
    }
    applyNavigation(intent);
  };

  const saveAndContinue = () => {
    if (pendingNavigation && saveEditor()) {
      applyNavigation(pendingNavigation);
    }
  };

  const discardAndContinue = () => {
    if (editingCase) loadCaseIntoForm(editingCase);
    if (pendingNavigation) applyNavigation(pendingNavigation);
  };

  const addPlanRelation = (caseRecordId: string, planId: string) => {
    const linkedPlanIds = savedRelations[caseRecordId] ?? [];
    if (linkedPlanIds.includes(planId)) return;
    const nextRelations = {
      ...savedRelations,
      [caseRecordId]: [...linkedPlanIds, planId],
    };
    try {
      saveCaseShowcaseMockPlanRelations(nextRelations);
      setSavedRelations(nextRelations);
      if (caseRecordId === activeCaseId) {
        setFormPlanIds([...(nextRelations[caseRecordId] ?? [])]);
      }
      setRelationError('');
    } catch {
      setRelationError('关联未保存，请重新选择重试。');
    }
  };

  const addFormPlan = (planId: string) => {
    if (editorMode === 'edit' && activeCaseId) {
      addPlanRelation(activeCaseId, planId);
      return;
    }
    setFormPlanIds((current) => [...new Set([...current, planId])]);
  };

  const removeFormPlan = (planId: string) => {
    if (editorMode === 'edit' && activeCaseId) {
      const nextRelations = {
        ...savedRelations,
        [activeCaseId]: (savedRelations[activeCaseId] ?? []).filter(
          (id) => id !== planId,
        ),
      };
      try {
        saveCaseShowcaseMockPlanRelations(nextRelations);
        setSavedRelations(nextRelations);
        setFormPlanIds([...nextRelations[activeCaseId]]);
        setRelationError('');
      } catch {
        setRelationError('关联未保存，请重新选择重试。');
      }
      return;
    }
    setFormPlanIds((current) => current.filter((id) => id !== planId));
  };

  const deleteCase = (record: CaseShowcaseRecord) => {
    modal.confirm({
      title: '删除案例',
      content: `删除「${record.name}」会同时移除它与方案的关联；案例只保存在当前浏览器，删除后无法恢复。`,
      okText: '删除案例',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => {
        const nextCases = cases.filter((item) => item.id !== record.id);
        const nextRelations = { ...savedRelations };
        delete nextRelations[record.id];
        try {
          saveCaseShowcaseMockState(nextCases, nextRelations);
        } catch {
          message.error('浏览器会话存储不可用，案例未删除');
          return;
        }
        setCases(nextCases);
        setSavedRelations(nextRelations);
        setExpandedCaseIds((current) =>
          current.filter((id) => id !== record.id),
        );
        if (activeCaseId === record.id) setEditorOpen(false);
        message.success('案例已删除');
      },
    });
  };

  const resetCases = () => {
    setCaseQueryInput('');
    setCaseQuery('');
    setCaseSourceInput('all');
    setCaseSource('all');
    setCaseRelationFilterInput('all');
    setCaseRelationFilter('all');
  };

  const applyCaseFilters = () => {
    setCaseQuery(caseQueryInput.trim());
    setCaseSource(caseSourceInput);
    setCaseRelationFilter(caseRelationFilterInput);
  };

  const refreshCases = () => {
    const nextCases = readCaseShowcaseMockCases(fixture);
    setCases(nextCases);
    setSavedRelations(readCaseShowcaseMockPlanRelations(fixture));
    setActiveCaseId((current) =>
      nextCases.some((record) => record.id === current)
        ? current
        : (nextCases[0]?.id ?? ''),
    );
    message.success('案例列表已刷新');
  };

  const resetCaseShareFilters = () => {
    setCaseShareQueryInput('');
    setCaseShareQuery('');
    setCaseShareModeInput('all');
    setCaseShareMode('all');
  };

  const applyCaseShareFilters = () => {
    setCaseShareQuery(caseShareQueryInput.trim());
    setCaseShareMode(caseShareModeInput);
  };

  const openShareEditor = (share?: CaseShareExample) => {
    setEditingShareId(share?.id ?? null);
    setShareDraftCaseIds(share?.caseIds ?? []);
    setShareDraftMode(share?.mode ?? 'shared');
    setShareDraftError('');
    setShareEditorOpen(true);
  };

  const saveShare = () => {
    if (shareDraftCaseIds.length === 0) {
      setShareDraftError('请至少选择一个案例。');
      return;
    }
    if (editingShareId) {
      setCaseShares((current) =>
        current.map((share) =>
          share.id === editingShareId
            ? { ...share, caseIds: shareDraftCaseIds, mode: shareDraftMode }
            : share,
        ),
      );
      message.success('案例分享已更新');
    } else {
      const nextNumber =
        Math.max(
          0,
          ...caseShares.map(
            (share) => Number(share.id.replace(/\D/g, '')) || 0,
          ),
        ) + 1;
      const id = `CS-${String(nextNumber).padStart(3, '0')}`;
      setCaseShares((current) => [
        { id, caseIds: shareDraftCaseIds, mode: shareDraftMode },
        ...current,
      ]);
      message.success(`已新建案例分享 ${id}`);
    }
    setShareEditorOpen(false);
  };

  const deleteShare = (share: CaseShareExample) => {
    modal.confirm({
      title: '删除案例分享',
      content: `删除「${share.id}」后，该条演示记录会从列表移除；案例库与方案关联不受影响。`,
      okText: '删除分享',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => {
        setCaseShares((current) =>
          current.filter((item) => item.id !== share.id),
        );
        if (detail?.kind === 'case-share' && detail.id === share.id) {
          setDetail(null);
        }
        message.success('案例分享已删除');
      },
    });
  };

  const openCaseEditor = (
    mode: 'create' | 'edit',
    record?: CaseShowcaseRecord,
  ) => {
    if (mode === 'edit' && record) {
      setEditorMode('edit');
      loadCaseIntoForm(record);
    } else {
      setEditorMode('create');
      setCaseForm({
        caseId: nextCaseId(cases),
        name: '',
        source: DEFAULT_CASE_SOURCE,
        tags: [],
        images: [],
      });
      setFormPlanIds([]);
    }
    setCaseFormError('');
    setUploadError('');
    setRelationError('');
    setEditorOpen(true);
  };

  const readImageFile = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () =>
        resolve(typeof reader.result === 'string' ? reader.result : '');
      reader.onerror = () => reject(new Error('图片读取失败'));
      reader.readAsDataURL(file);
    });

  const canShrinkImage = () => {
    try {
      return Boolean(document.createElement('canvas').getContext('2d'));
    } catch {
      return false;
    }
  };

  /** 本地上传的图片按 720px 长边压缩后再写入会话，避免演示数据过大。 */
  const shrinkImage = async (dataUrl: string) => {
    try {
      const image = new Image();
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error('图片解码失败'));
        image.src = dataUrl;
      });
      const scale = Math.min(1, 720 / Math.max(image.width, image.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      const context = canvas.getContext('2d');
      if (!context) return dataUrl;
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const shrunk = canvas.toDataURL('image/jpeg', 0.82);
      return shrunk.startsWith('data:image/jpeg') ? shrunk : dataUrl;
    } catch {
      return dataUrl;
    }
  };

  const handleImageFiles = async (files: File[]) => {
    if (files.length === 0) return;
    setUploadError('');
    const uploaded: string[] = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        setUploadError('仅支持上传图片文件。');
        continue;
      }
      try {
        const dataUrl = await readImageFile(file);
        uploaded.push(canShrinkImage() ? await shrinkImage(dataUrl) : dataUrl);
      } catch {
        setUploadError('图片读取失败，请重新上传。');
      }
    }
    if (uploaded.length === 0) return;
    setCaseForm((current) => ({
      ...current,
      images: [
        ...current.images,
        ...uploaded.filter((image) => !current.images.includes(image)),
      ],
    }));
  };

  const removeCaseImage = (index: number) => {
    setCaseForm((current) => ({
      ...current,
      images: current.images.filter((_, imageIndex) => imageIndex !== index),
    }));
  };

  const availableTags = useMemo(
    () =>
      [...new Set(cases.flatMap((record) => record.tags))].sort((left, right) =>
        left.localeCompare(right, 'zh-CN'),
      ),
    [cases],
  );

  const planOptionsFor = (
    excludePlanIds: readonly string[],
    relations: Record<string, string[]>,
  ) =>
    fixture.catalog.plans
      .filter((plan) => !excludePlanIds.includes(plan.id))
      .map((plan) => ({
        plan,
        linkedCaseCount: cases.filter((record) =>
          (relations[record.id] ?? []).includes(plan.id),
        ).length,
      }));

  const renderCaseList = () => (
    <>
      <ManagementFilterBar
        label="案例管理筛选与操作"
        fieldsetAttributes={{ style: { marginBottom: 0 } }}
        filters={
          <>
            <label className={styles.searchField}>
              <span className={styles.visuallyHidden}>搜索案例名称或编号</span>
              <input
                aria-label="搜索案例名称或编号"
                type="search"
                placeholder="搜索案例名称或编号"
                value={caseQueryInput}
                onChange={(event) =>
                  setCaseQueryInput(event.currentTarget.value)
                }
                onKeyDown={(event) => {
                  if (event.key === 'Enter') applyCaseFilters();
                }}
              />
            </label>
            <label className={styles.selectField} htmlFor="case-source-filter">
              <span className={styles.visuallyHidden}>内容来源</span>
              <NativeSelect
                id="case-source-filter"
                aria-label="内容来源"
                value={caseSourceInput}
                onChange={(event) =>
                  setCaseSourceInput(event.currentTarget.value)
                }
              >
                <option value="all">全部来源</option>
                {CASE_SOURCE_OPTIONS.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </NativeSelect>
            </label>
            <label
              className={styles.selectField}
              htmlFor="case-plan-relation-filter"
            >
              <span className={styles.visuallyHidden}>方案关联状态</span>
              <NativeSelect
                id="case-plan-relation-filter"
                aria-label="方案关联状态"
                value={caseRelationFilterInput}
                onChange={(event) =>
                  setCaseRelationFilterInput(event.currentTarget.value)
                }
              >
                <option value="all">全部关联状态</option>
                <option value="linked">已关联</option>
                <option value="unlinked">待维护</option>
              </NativeSelect>
            </label>
          </>
        }
        actions={
          <>
            <button
              className={styles.secondaryButton}
              type="button"
              onClick={resetCases}
            >
              重置
            </button>
            <button
              className={styles.primaryButton}
              type="button"
              onClick={applyCaseFilters}
            >
              查询
            </button>
            <button
              className={styles.secondaryButton}
              type="button"
              onClick={refreshCases}
            >
              <ReloadOutlined aria-hidden="true" />
              刷新
            </button>
            <button
              className={styles.primaryButton}
              type="button"
              onClick={() => openCaseEditor('create')}
            >
              <PlusOutlined aria-hidden="true" />
              新增案例
            </button>
          </>
        }
      />

      <div className={styles.tableViewport}>
        <table className={styles.table} aria-label="案例管理列表">
          <thead>
            <tr>
              <th scope="col" className={styles.expandCell}>
                <span className={styles.visuallyHidden}>展开关联方案</span>
              </th>
              <th scope="col">案例编号</th>
              <th scope="col">案例名称</th>
              <th scope="col">内容来源</th>
              <th scope="col">分类标签</th>
              <th scope="col">效果图</th>
              <th scope="col">已关联方案</th>
              <th scope="col">素材版本</th>
              <th scope="col">关系检查</th>
              <th scope="col">操作</th>
            </tr>
          </thead>
          <tbody>
            {filteredCases.map((record) => {
              const planIds = savedRelations[record.id] ?? [];
              const linkedPlans = planIds
                .map((planId) =>
                  fixture.catalog.plans.find((plan) => plan.id === planId),
                )
                .filter((plan): plan is PlanCatalogRecord => Boolean(plan));
              const planNames = linkedPlans.map((plan) => plan.name);
              const expanded = expandedCaseIds.includes(record.id);
              return (
                <Fragment key={record.id}>
                  <tr data-expanded={expanded}>
                    <td className={styles.expandCell}>
                      <button
                        className={styles.expandButton}
                        type="button"
                        aria-label={`${expanded ? '收起' : '展开'}${record.name}的关联方案`}
                        aria-expanded={expanded}
                        aria-controls={
                          expanded ? `case-relations-${record.id}` : undefined
                        }
                        onClick={() =>
                          setExpandedCaseIds((current) =>
                            expanded
                              ? current.filter((id) => id !== record.id)
                              : [...current, record.id],
                          )
                        }
                      >
                        <span
                          className={styles.expandIcon}
                          aria-hidden="true"
                        />
                      </button>
                    </td>
                    <td className={styles.codeCell}>{record.caseId}</td>
                    <td>{record.name}</td>
                    <td>{record.source ?? DEFAULT_CASE_SOURCE}</td>
                    <td
                      className={styles.planNames}
                      title={caseTagLabels(record).join('、')}
                    >
                      {caseTagLabels(record).join('、') || '未分类'}
                    </td>
                    <td>
                      <div className={styles.galleryCell}>
                        <HoverImagePreview
                          sources={[record.heroImage, ...record.gallery]}
                          alt={`${record.name} 案例效果图`}
                          frameClassName={styles.galleryThumb}
                        />
                        <span>{record.gallery.length}</span>
                      </div>
                    </td>
                    <td
                      className={styles.planNames}
                      title={planNames.join('、')}
                    >
                      {planNames.length > 0 ? planNames.join('、') : '—'}
                    </td>
                    <td>V{record.version}</td>
                    <td>
                      <span className={styles.stateLabel}>
                        {planIds.length > 0
                          ? `已关联 ${planIds.length} 个方案`
                          : '待维护'}
                      </span>
                    </td>
                    <td>
                      <div className={styles.rowActions}>
                        <button
                          className={styles.textButton}
                          type="button"
                          onClick={() => openCaseEditor('edit', record)}
                        >
                          编辑
                        </button>
                        <button
                          className={styles.textButton}
                          type="button"
                          onClick={() => deleteCase(record)}
                        >
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expanded ? (
                    <tr className={styles.expandedRow}>
                      <td colSpan={10}>
                        <section
                          id={`case-relations-${record.id}`}
                          className={styles.expandedRelations}
                          aria-label={`${record.name}的关联方案`}
                        >
                          {linkedPlans.length > 0 ? (
                            <table
                              className={styles.expandedTable}
                              aria-label={`${record.name}已关联方案`}
                            >
                              <thead>
                                <tr>
                                  <th scope="col">方案编号</th>
                                  <th scope="col">方案名称</th>
                                  <th scope="col">同方案关联案例</th>
                                  <th scope="col">版本 / 状态</th>
                                </tr>
                              </thead>
                              <tbody>
                                {linkedPlans.map((plan) => (
                                  <tr key={plan.id}>
                                    <td className={styles.codeCell}>
                                      {plan.code ?? plan.id}
                                    </td>
                                    <td>{plan.name}</td>
                                    <td>
                                      {cases
                                        .filter((item) =>
                                          (
                                            savedRelations[item.id] ?? []
                                          ).includes(plan.id),
                                        )
                                        .map((item) => item.name)
                                        .join('、')}
                                    </td>
                                    <td>
                                      {plan.currentRevisionId ?? 'Fixture'} ·{' '}
                                      {plan.status === 'inactive'
                                        ? '停用'
                                        : '启用'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div className={styles.emptyState}>
                              暂无关联方案。
                            </div>
                          )}
                          {relationError ? (
                            <div className={styles.inlineError} role="alert">
                              {relationError}
                            </div>
                          ) : null}
                          <PlanLinkAdder
                            label={`为${record.name}添加关联方案`}
                            options={planOptionsFor(
                              savedRelations[record.id] ?? [],
                              savedRelations,
                            )}
                            onPick={(planId) =>
                              addPlanRelation(record.id, planId)
                            }
                          />
                        </section>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {filteredCases.length === 0 ? (
          <div className={styles.emptyState} role="status">
            未找到符合条件的案例。调整搜索词或重置筛选后再试。
          </div>
        ) : null}
      </div>
    </>
  );

  const renderPlanEditor = () => {
    const draftForCase =
      editorMode === 'edit' && activeCaseId
        ? { [activeCaseId]: formPlanIds }
        : {};
    const otherLinkedCases = new Map<string, string[]>();
    for (const record of cases) {
      for (const planId of draftForCase[record.id] ??
        savedRelations[record.id] ??
        []) {
        const linked = otherLinkedCases.get(planId) ?? [];
        linked.push(record.name);
        otherLinkedCases.set(planId, linked);
      }
    }
    const linkedPlans = formPlanIds
      .map((planId) => fixture.catalog.plans.find((plan) => plan.id === planId))
      .filter((plan): plan is PlanCatalogRecord => Boolean(plan));

    return (
      <section className={styles.caseFormPlans} aria-label="关联方案">
        <h3>关联方案</h3>
        <PlanLinkAdder
          label="搜索并添加关联方案"
          options={planOptionsFor(formPlanIds, {
            ...savedRelations,
            ...draftForCase,
          })}
          onPick={addFormPlan}
        />
        <div className={styles.tableViewport}>
          <table className={styles.relationTable} aria-label="已关联方案">
            <thead>
              <tr>
                <th scope="col">方案编号 / 名称</th>
                <th scope="col">同方案关联案例</th>
                <th scope="col">版本 / 状态</th>
                <th scope="col">操作</th>
              </tr>
            </thead>
            <tbody>
              {linkedPlans.map((plan) => (
                <tr key={plan.id}>
                  <td>
                    <span className={styles.codeCell}>
                      {plan.code ?? plan.id}
                    </span>
                    <span className={styles.subCell}>{plan.name}</span>
                  </td>
                  <td>
                    {(otherLinkedCases.get(plan.id) ?? []).join('、') || '—'}
                  </td>
                  <td>
                    <span className={styles.subCell}>
                      {plan.currentRevisionId ?? 'Fixture'} ·{' '}
                      {plan.status === 'inactive' ? '停用' : '启用'}
                    </span>
                  </td>
                  <td>
                    <button
                      className={styles.textButton}
                      type="button"
                      onClick={() => removeFormPlan(plan.id)}
                    >
                      移除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {linkedPlans.length === 0 ? (
            <div className={styles.emptyState}>暂无关联方案。</div>
          ) : null}
        </div>
        {relationError ? (
          <div className={styles.inlineError} role="alert">
            {relationError}
          </div>
        ) : null}
      </section>
    );
  };

  const renderCaseShares = () => (
    <>
      <ManagementFilterBar
        label="案例分享筛选与操作"
        fieldsetAttributes={{ style: { marginBottom: 0 } }}
        filters={
          <>
            <label className={styles.searchField}>
              <span className={styles.visuallyHidden}>
                搜索案例分享编号或案例
              </span>
              <input
                aria-label="搜索案例分享编号或案例"
                type="search"
                placeholder="搜索分享编号或案例名称"
                value={caseShareQueryInput}
                onChange={(event) =>
                  setCaseShareQueryInput(event.currentTarget.value)
                }
                onKeyDown={(event) => {
                  if (event.key === 'Enter') applyCaseShareFilters();
                }}
              />
            </label>
            <label
              className={styles.selectField}
              htmlFor="case-share-mode-filter"
            >
              <span className={styles.visuallyHidden}>清单组合方式</span>
              <NativeSelect
                id="case-share-mode-filter"
                aria-label="清单组合方式"
                value={caseShareModeInput}
                onChange={(event) =>
                  setCaseShareModeInput(event.currentTarget.value)
                }
              >
                <option value="all">全部组合</option>
                <option value="shared">共用清单</option>
                <option value="separate">不同清单</option>
              </NativeSelect>
            </label>
          </>
        }
        actions={
          <>
            <button
              className={styles.secondaryButton}
              type="button"
              onClick={resetCaseShareFilters}
            >
              重置
            </button>
            <button
              className={styles.primaryButton}
              type="button"
              onClick={applyCaseShareFilters}
            >
              查询
            </button>
            <button
              className={styles.primaryButton}
              type="button"
              onClick={() => openShareEditor()}
            >
              <PlusOutlined aria-hidden="true" />
              新建分享
            </button>
          </>
        }
      />
      <div className={styles.tableViewport}>
        <table className={styles.table} aria-label="案例分享管理列表">
          <thead>
            <tr>
              <th scope="col">分享编号</th>
              <th scope="col">案例效果</th>
              <th scope="col">效果数</th>
              <th scope="col">设备清单组合</th>
              <th scope="col">关联方案</th>
              <th scope="col">清单数</th>
              <th scope="col">内容来源</th>
              <th scope="col">清单分组</th>
              <th scope="col">操作</th>
            </tr>
          </thead>
          <tbody>
            {filteredCaseShares.map((share) => {
              const relatedCases = share.caseIds
                .map((id) => cases.find((record) => record.id === id))
                .filter((record): record is CaseShowcaseRecord =>
                  Boolean(record),
                );
              const planIds =
                share.mode === 'shared'
                  ? commonPlanIds(share.caseIds, savedRelations)
                  : [
                      ...new Set(
                        share.caseIds.flatMap((id) => savedRelations[id] ?? []),
                      ),
                    ];
              const planNames = planIds
                .map(
                  (id) =>
                    fixture.catalog.plans.find((plan) => plan.id === id)?.name,
                )
                .filter((name): name is string => Boolean(name));
              return (
                <tr key={share.id}>
                  <td className={styles.codeCell}>{share.id}</td>
                  <td
                    className={styles.planNames}
                    title={relatedCases.map((item) => item.name).join('、')}
                  >
                    {relatedCases.map((item) => item.name).join('、') || '—'}
                  </td>
                  <td>
                    {relatedCases.reduce(
                      (count, item) => count + item.gallery.length,
                      0,
                    )}
                  </td>
                  <td>{share.mode === 'shared' ? '共用清单' : '不同清单'}</td>
                  <td className={styles.planNames} title={planNames.join('、')}>
                    {planNames.join('、') || '暂无关联方案'}
                  </td>
                  <td>{share.mode === 'shared' ? 1 : relatedCases.length}</td>
                  <td>案例 Fixture</td>
                  <td>{share.mode === 'shared' ? '共用清单' : '按案例分组'}</td>
                  <td>
                    <div className={styles.rowActions}>
                      <button
                        className={styles.textButton}
                        type="button"
                        onClick={() =>
                          setDetail({ kind: 'case-share', id: share.id })
                        }
                      >
                        查看组合
                      </button>
                      <button
                        className={styles.textButton}
                        type="button"
                        onClick={() => openShareEditor(share)}
                      >
                        编辑
                      </button>
                      <button
                        className={styles.textButton}
                        type="button"
                        onClick={() => deleteShare(share)}
                      >
                        删除
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredCaseShares.length === 0 ? (
          <div className={styles.emptyState} role="status">
            没有符合条件的案例分享演示记录。
          </div>
        ) : null}
      </div>
    </>
  );

  const renderShareDetails = (record: CaseShareExample) => {
    const relatedCases = record.caseIds
      .map((id) => cases.find((item) => item.id === id))
      .filter((item): item is CaseShowcaseRecord => Boolean(item));
    const planIds =
      record.mode === 'shared'
        ? commonPlanIds(record.caseIds, savedRelations)
        : [
            ...new Set(
              record.caseIds.flatMap((id) => savedRelations[id] ?? []),
            ),
          ];
    const sharePlans = planIds
      .map((id) => fixture.catalog.plans.find((plan) => plan.id === id))
      .filter((plan): plan is PlanCatalogRecord => Boolean(plan));
    return (
      <>
        <div className={styles.detailStatus}>前端 Mock 演示组合</div>
        <dl className={styles.detailGrid}>
          <dt>分享编号</dt>
          <dd>{record.id}</dd>
          <dt>组合方式</dt>
          <dd>{record.mode === 'shared' ? '共用清单' : '不同清单'}</dd>
          <dt>案例范围</dt>
          <dd>{relatedCases.map((item) => item.name).join('、')}</dd>
          <dt>方案关系</dt>
          <dd>根据当前页面的案例—方案 Fixture 展示</dd>
        </dl>
        {record.mode === 'shared' ? (
          <section className={styles.detailSection}>
            <h3>共用清单</h3>
            {sharePlans.map((plan) => (
              <div className={styles.detailPlan} key={plan.id}>
                <h4>{planLabel(plan)}</h4>
                <p>清单设备（Fixture）</p>
                <ul>
                  {planDeviceNames(fixture, plan.id).map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              </div>
            ))}
            {sharePlans.length === 0 ? <p>当前没有共同关联的方案。</p> : null}
          </section>
        ) : (
          <section className={styles.detailSection}>
            <h3>按案例区分清单</h3>
            {relatedCases.map((recordCase) => {
              const casePlans = (savedRelations[recordCase.id] ?? [])
                .map((id) =>
                  fixture.catalog.plans.find((plan) => plan.id === id),
                )
                .filter((plan): plan is PlanCatalogRecord => Boolean(plan));
              return (
                <div className={styles.detailPlan} key={recordCase.id}>
                  <h4>
                    {recordCase.caseId} · {recordCase.name}
                  </h4>
                  {casePlans.map((plan) => (
                    <div key={plan.id}>
                      <p>{planLabel(plan)} · 可选方案</p>
                      <ul>
                        {planDeviceNames(fixture, plan.id).map((name) => (
                          <li key={name}>{name}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  {casePlans.length === 0 ? (
                    <p>当前案例没有关联方案。</p>
                  ) : null}
                </div>
              );
            })}
          </section>
        )}
        <PendingShareTerms />
      </>
    );
  };

  return (
    <section className={styles.root} aria-label="案例与分享后台">
      <nav className={styles.tabs} aria-label="案例与分享后台模块">
        <button
          aria-current={tab === 'cases' ? 'page' : undefined}
          className={tab === 'cases' ? styles.tabActive : styles.tab}
          type="button"
          onClick={() => requestNavigation({ type: 'tab', tab: 'cases' })}
        >
          案例管理
        </button>
        <button
          aria-current={tab === 'case-shares' ? 'page' : undefined}
          className={tab === 'case-shares' ? styles.tabActive : styles.tab}
          type="button"
          onClick={() => requestNavigation({ type: 'tab', tab: 'case-shares' })}
        >
          案例分享
        </button>
      </nav>

      <section id="case-admin-panel" className={styles.tabPanel}>
        {tab === 'cases' ? renderCaseList() : renderCaseShares()}
      </section>

      <Drawer
        aria-label={editorMode === 'edit' ? '编辑案例' : '新增案例'}
        rootClassName={styles.drawerRoot}
        open={editorOpen && tab === 'cases'}
        title={
          editorMode === 'edit' && activeCase
            ? `编辑案例 · ${activeCase.caseId}`
            : '新增案例'
        }
        size="min(760px, 94vw)"
        onClose={() => requestNavigation({ type: 'list' })}
        footer={
          <div className={styles.relationFooter}>
            {caseFormError ? (
              <span className={styles.inlineError} role="alert">
                {caseFormError}
              </span>
            ) : (
              <span role="status">
                {isEditorDirty ? '有未保存更改' : '没有待保存的修改'}
              </span>
            )}
            <div className={styles.relationSave}>
              <button
                className={styles.secondaryButton}
                type="button"
                onClick={() => requestNavigation({ type: 'list' })}
              >
                关闭
              </button>
              <button
                className={styles.primaryButton}
                type="button"
                disabled={editorMode === 'edit' && !isEditorDirty}
                onClick={saveEditor}
              >
                保存案例
              </button>
            </div>
          </div>
        }
      >
        <form
          className={styles.caseForm}
          onSubmit={(event) => {
            event.preventDefault();
            saveEditor();
          }}
        >
          {editorMode === 'edit' ? (
            <nav className={styles.caseNavigation} aria-label="切换案例">
              <span>
                {activeCasePosition >= 0
                  ? `第 ${activeCasePosition + 1} / ${filteredCases.length} 个案例`
                  : '当前案例不在列表筛选结果中'}
              </span>
              <button
                className={styles.secondaryButton}
                type="button"
                disabled={!previousCase}
                onClick={() =>
                  previousCase &&
                  requestNavigation({ type: 'case', caseId: previousCase.id })
                }
              >
                上一个案例
              </button>
              <button
                className={styles.secondaryButton}
                type="button"
                disabled={!nextCase}
                onClick={() =>
                  nextCase &&
                  requestNavigation({ type: 'case', caseId: nextCase.id })
                }
              >
                下一个案例
              </button>
            </nav>
          ) : null}

          <label className={styles.caseFormField}>
            <span>案例编号</span>
            <input
              aria-label="案例编号"
              value={caseForm.caseId}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setCaseForm((current) => ({ ...current, caseId: value }));
              }}
            />
          </label>
          <label className={styles.caseFormField}>
            <span>案例名称</span>
            <input
              aria-label="案例名称"
              value={caseForm.name}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setCaseForm((current) => ({ ...current, name: value }));
              }}
            />
          </label>
          <label className={styles.caseFormField} htmlFor="case-form-source">
            <span>内容来源</span>
            <NativeSelect
              id="case-form-source"
              aria-label="内容来源"
              value={caseForm.source}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setCaseForm((current) => ({ ...current, source: value }));
              }}
            >
              {CASE_SOURCE_OPTIONS.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </NativeSelect>
          </label>

          <fieldset className={styles.caseFormPlans} aria-label="分类标签">
            <legend className={styles.caseFormLegend}>分类标签</legend>
            <div className={styles.caseFormTags}>
              {availableTags.length ? (
                availableTags.map((tag) => {
                  const selected = caseForm.tags.includes(tag);
                  return (
                    <button
                      className={`${styles.caseFormTag} ${selected ? styles.caseFormTagActive : ''}`}
                      aria-pressed={selected}
                      key={tag}
                      type="button"
                      onClick={() =>
                        setCaseForm((current) => ({
                          ...current,
                          tags: selected
                            ? current.tags.filter((item) => item !== tag)
                            : [...current.tags, tag],
                        }))
                      }
                    >
                      {tag}
                    </button>
                  );
                })
              ) : (
                <span className={styles.caseFormHint}>
                  暂时没有可用标签，保存后可在其他案例上复用。
                </span>
              )}
            </div>
            <span className={styles.caseFormHint}>
              第一个选中的标签作为案例库的默认分类。
            </span>
          </fieldset>

          <fieldset className={styles.caseFormImages}>
            <legend>效果图</legend>
            <div className={styles.caseFormUploadRow}>
              <label className={styles.caseFormUpload}>
                <input
                  aria-label="上传效果图"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(event) => {
                    const files = Array.from(event.currentTarget.files ?? []);
                    event.currentTarget.value = '';
                    void handleImageFiles(files);
                  }}
                />
                <span>本地上传</span>
              </label>
              <span className={styles.caseFormHint}>
                从本机选择图片，可多选，第一张作为封面
              </span>
            </div>
            {uploadError ? (
              <p className={styles.inlineError} role="alert">
                {uploadError}
              </p>
            ) : null}
            {caseForm.images.length > 0 ? (
              <ul className={styles.caseFormImageGrid}>
                {caseForm.images.map((image, index) => (
                  <li className={styles.caseFormImage} key={image}>
                    <img src={image} alt={`效果图 ${index + 1}`} />
                    <button
                      className={styles.textButton}
                      type="button"
                      onClick={() => removeCaseImage(index)}
                    >
                      移除
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={styles.caseFormHint}>尚未上传效果图。</p>
            )}
          </fieldset>

          {renderPlanEditor()}
        </form>
      </Drawer>

      <Drawer
        aria-label={detailTitle}
        rootClassName={styles.drawerRoot}
        open={detail !== null}
        title={detailTitle}
        size="min(660px, 92vw)"
        onClose={() => setDetail(null)}
      >
        {activeCaseShare ? renderShareDetails(activeCaseShare) : null}
      </Drawer>

      <Modal
        rootClassName={styles.modalRoot}
        title={
          editingShareId ? `编辑案例分享 · ${editingShareId}` : '新建案例分享'
        }
        open={shareEditorOpen}
        onCancel={() => setShareEditorOpen(false)}
        destroyOnHidden
        footer={
          <div className={styles.modalActions}>
            <button
              className={styles.secondaryButton}
              type="button"
              onClick={() => setShareEditorOpen(false)}
            >
              取消
            </button>
            <button
              className={styles.primaryButton}
              type="button"
              onClick={saveShare}
            >
              {editingShareId ? '保存分享' : '创建分享'}
            </button>
          </div>
        }
      >
        <div className={styles.shareEditor}>
          <p className={styles.shareEditorHint}>
            选择要放进这份分享的案例；共用清单会把设备合并成一份，不同清单按案例分开。
          </p>
          {shareDraftError ? (
            <p className={styles.inlineError} role="alert">
              {shareDraftError}
            </p>
          ) : null}
          <fieldset className={styles.shareEditorField}>
            <legend>选择案例</legend>
            <Checkbox.Group
              value={shareDraftCaseIds}
              onChange={(values) => {
                setShareDraftCaseIds(values.map(String));
                setShareDraftError('');
              }}
              options={cases.map((record) => ({
                label: `${record.caseId} · ${record.name}`,
                value: record.id,
              }))}
            />
          </fieldset>
          <fieldset className={styles.shareEditorField}>
            <legend>设备清单组合</legend>
            <Radio.Group
              value={shareDraftMode}
              onChange={(event) =>
                setShareDraftMode(event.target.value as CaseShareMode)
              }
            >
              <Radio value="shared">共用清单</Radio>
              <Radio value="separate">不同清单</Radio>
            </Radio.Group>
          </fieldset>
        </div>
      </Modal>

      {pendingNavigation ? (
        <Modal
          rootClassName={styles.modalRoot}
          footer={
            <div className={styles.modalActions}>
              <button
                className={styles.secondaryButton}
                type="button"
                onClick={() => setPendingNavigation(null)}
              >
                取消
              </button>
              <button
                className={styles.secondaryButton}
                type="button"
                onClick={discardAndContinue}
              >
                放弃更改并继续
              </button>
              <button
                className={styles.primaryButton}
                type="button"
                onClick={saveAndContinue}
              >
                保存并继续
              </button>
            </div>
          }
          open
          title="当前案例有未保存更改"
          onCancel={() => setPendingNavigation(null)}
        >
          <p>离开当前案例前，选择保存关联、放弃本次更改，或取消并继续编辑。</p>
        </Modal>
      ) : null}
    </section>
  );

  function PendingShareTerms() {
    const pendingItems = [
      '访问权限',
      '有效期',
      '撤回',
      '访问记录',
      '更新规则',
      '会签通知',
    ];
    return (
      <section
        className={styles.pendingTerms}
        aria-labelledby="pending-terms-title"
      >
        <h3 id="pending-terms-title">分享规则 · 待业务确认</h3>
        <ul>
          {pendingItems.map((item) => (
            <li key={item}>
              <span>{item}</span>
              <span>待确认</span>
            </li>
          ))}
        </ul>
      </section>
    );
  }
}
